import { useSyncExternalStore } from 'react';
import { haptics } from '../lib/platform';
import s from './Toast.module.css';

interface ToastItem {
  id: number;
  message: string;
  tone: 'neutral' | 'error';
  action?: { label: string; run: () => void };
}

let toasts: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

/** Shows a short message at the bottom. Errors stay longer. */
export function toast(
  message: string,
  opts: { tone?: 'neutral' | 'error'; action?: ToastItem['action'] } = {},
) {
  const item: ToastItem = {
    id: nextId++,
    message,
    tone: opts.tone ?? 'neutral',
    action: opts.action,
  };
  toasts = [...toasts.slice(-2), item];
  emit();
  if (item.tone === 'error') void haptics.notify('error');
  setTimeout(() => dismiss(item.id), item.tone === 'error' ? 6000 : 2600);
}

function dismiss(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export function Toasts() {
  const items = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => toasts,
    () => toasts,
  );
  return (
    <div className={s.stack} aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`${s.toast} ${t.tone === 'error' ? s.error : ''}`} role="status">
          <span>{t.message}</span>
          {t.action && (
            <button
              type="button"
              className={s.action}
              onClick={() => {
                t.action!.run();
                dismiss(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
